import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import { ArrowLeft, Maximize2, Minimize2 } from 'lucide-react';

const IFRAME_SRC = `${process.env.PUBLIC_URL || ''}/games/esoroban-ninja/index.html`;

function getFullscreenElement() {
  return (
    document.fullscreenElement ||
    document.webkitFullscreenElement ||
    document.mozFullScreenElement ||
    document.msFullscreenElement ||
    null
  );
}

const StudentEsorobanNinjaGame = () => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const backArrow = isRTL ? 'rotate-180' : '';

  const gameWrapRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const onChange = () => {
      const el = getFullscreenElement();
      setIsFullscreen(!!el && gameWrapRef.current && el === gameWrapRef.current);
    };
    document.addEventListener('fullscreenchange', onChange);
    document.addEventListener('webkitfullscreenchange', onChange);
    document.addEventListener('mozfullscreenchange', onChange);
    document.addEventListener('MSFullscreenChange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      document.removeEventListener('webkitfullscreenchange', onChange);
      document.removeEventListener('mozfullscreenchange', onChange);
      document.removeEventListener('MSFullscreenChange', onChange);
    };
  }, []);

  const toggleFullscreen = useCallback(async () => {
    const node = gameWrapRef.current;
    if (!node) return;

    try {
      if (getFullscreenElement()) {
        if (document.exitFullscreen) await document.exitFullscreen();
        else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
        else if (document.mozCancelFullScreen) document.mozCancelFullScreen();
        else if (document.msExitFullscreen) document.msExitFullscreen();
      } else {
        if (node.requestFullscreen) await node.requestFullscreen();
        else if (node.webkitRequestFullscreen) node.webkitRequestFullscreen();
        else if (node.mozRequestFullScreen) node.mozRequestFullScreen();
        else if (node.msRequestFullscreen) node.msRequestFullscreen();
      }
    } catch {
      /* ignore */
    }
  }, []);

  return (
    <div
      className="relative mx-auto flex w-full max-w-6xl flex-col gap-4 px-0 pb-6 sm:pb-8"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-3">
          <Link
            to="/student/games"
            className="inline-flex items-center gap-2 rounded-xl border-2 border-slate-900/10 bg-white px-3 py-2 text-sm font-bold text-slate-800 shadow-sm transition hover:bg-slate-50"
          >
            <ArrowLeft className={`h-4 w-4 shrink-0 ${backArrow}`} aria-hidden />
            {t('studentGames.backToList')}
          </Link>
          <h1 className="text-xl font-black text-slate-900 sm:text-2xl">
            {t('studentGames.esorobanNinjaTitle')}
          </h1>
        </div>
      </div>

      <p className="max-w-3xl text-sm font-semibold leading-relaxed text-slate-600">
        {t('studentGames.esorobanNinjaHint')}
      </p>

      <div
        ref={gameWrapRef}
        className="relative overflow-hidden rounded-[1.35rem] border-[3px] border-slate-900/10 bg-slate-950 shadow-[0_12px_0_0_rgba(15,23,42,0.08)] [&:fullscreen]:flex [&:fullscreen]:h-screen [&:fullscreen]:min-h-0 [&:fullscreen]:flex-col [&:fullscreen]:rounded-none [&:fullscreen]:border-0 [&:fullscreen]:shadow-none"
      >
        <button
          type="button"
          onClick={toggleFullscreen}
          className={`absolute end-3 top-[3.75rem] z-10 inline-flex items-center gap-2 rounded-xl border-2 border-white/20 bg-slate-900/80 px-3 py-2 text-xs font-black text-white shadow-lg backdrop-blur-sm transition hover:bg-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 sm:text-sm ${
            isFullscreen ? 'ring-2 ring-cyan-400/50' : ''
          }`}
          aria-pressed={isFullscreen}
          aria-label={
            isFullscreen ? t('studentGames.exitFullscreen') : t('studentGames.fullscreen')
          }
        >
          {isFullscreen ? (
            <Minimize2 className="h-4 w-4 shrink-0 sm:h-5 sm:w-5" aria-hidden />
          ) : (
            <Maximize2 className="h-4 w-4 shrink-0 sm:h-5 sm:w-5" aria-hidden />
          )}
          <span className="hidden sm:inline">
            {isFullscreen ? t('studentGames.exitFullscreen') : t('studentGames.fullscreen')}
          </span>
        </button>

        <iframe
          title={t('studentGames.esorobanNinjaTitle')}
          src={IFRAME_SRC}
          className={`block w-full flex-1 border-0 ${isFullscreen ? 'min-h-0' : ''}`}
          style={
            isFullscreen
              ? { height: '100%', minHeight: 0, flex: 1 }
              : { height: 'min(85dvh, 920px)', minHeight: 'min(70dvh, 560px)' }
          }
          allow="autoplay; fullscreen"
        />
      </div>
    </div>
  );
};

export default StudentEsorobanNinjaGame;
